// src/modules/assinaturas/estabelecimento/AssinaturaDetalhe.tsx
import { useCallback, useEffect, useState } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { Modal } from '../../../components/Modal';
import AvatarUser from '../../../components/AvatarUser';
import { ServicosService } from '../../servicos/Service';
import { getPlanBillingLabel, getPlanPriceSuffix } from '../../planos/cliente/planosUtils';
import { useAssinaturasContext } from './Context';
import AssinaturasPeriodos from './Periodos';
import AssinaturasRecorrencias from './Recorrencias';
import { AssinaturasService } from './Service';
import { BillingStatusTag, formatCivilDateBR, formatCurrencyBRL, formatDateBR, PlanoService } from './utils/periodosUtils';
import './Assinaturas.css';
import { FilePen } from 'lucide-react';

const assinaturasService = new AssinaturasService();
const servicosService = new ServicosService();

type ConfirmAction = 'cancel' | 'remove' | null;

const RECURRENCE_LABELS: Record<string, string> = {
    semanal: 'por semana',
    mensal: 'por mês',
    total: 'no total',
};

export default function AssinaturaDetalhe() {
    const { id, action, setParams, clear } = useParamsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { user } = useAuthContext();
    const { reloadData } = useAssinaturasContext();

    const [assinatura, setAssinatura] = useState<any>(null);
    const [servicesDict, setServicesDict] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
    const [processing, setProcessing] = useState(false);

    // A API so permite cancelar e remover para root e admin.
    const canManage = !!(user?.admin || user?.root);

    const loadAssinatura = useCallback(async () => {
        if (!id) return;
        try {
            const { data } = await assinaturasService.getInfoById(id);
            setAssinatura(data || null);
        } catch (e) {
            console.error('Erro ao carregar assinatura:', e);
            setAssinatura(null);
        }
    }, [id]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([
                loadAssinatura(),
                servicosService
                    .findAll()
                    .then((r: any) => {
                        const dict: Record<string, string> = {};
                        (r.data || []).forEach((s: any) => {
                            if (s?._id && s?.name) dict[s._id] = s.name;
                        });
                        setServicesDict(dict);
                    })
                    .catch((e: any) => console.error('Erro ao carregar serviços:', e)),
            ]);
            setLoading(false);
        };

        load();
    }, [loadAssinatura]);

    const handleConfirm = async () => {
        try {
            setProcessing(true);
            if (confirmAction === 'cancel') {
                await assinaturasService.update(id, { status: 0 });
                successSnackbar('Assinatura cancelada.');
                setConfirmAction(null);
                await loadAssinatura();
                reloadData();
            } else {
                await assinaturasService.delete(id);
                successSnackbar('Assinatura removida.');
                reloadData();
                clear();
            }
        } catch (e: any) {
            const message = e?.response?.data?.message;
            errorSnackbar(
                (Array.isArray(message) ? message.join(', ') : message) ||
                    `Não foi possível ${confirmAction === 'cancel' ? 'cancelar' : 'remover'} a assinatura.`,
            );
            setConfirmAction(null);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="ass-page space-y-3 px-2" aria-busy="true">
                <div className="sf-skeleton h-24" />
                <div className="sf-skeleton h-48" />
            </div>
        );
    }

    if (!assinatura) {
        return (
            <div className="sf-state">
                <FilePen aria-hidden />
                <h2>Assinatura não encontrada</h2>
                <p>Ela pode ter sido removida.</p>
                <button type="button" className="sf-link" onClick={clear}>
                    Voltar para assinaturas
                </button>
            </div>
        );
    }

    const plan = assinatura.planoSnapshot || assinatura.plano || assinatura.plan || {};
    const clientName =
        assinatura.clientName || assinatura.clienteNome || assinatura.client?.name || assinatura.cliente?.name || 'Cliente não identificado';
    const planName = assinatura.planName || assinatura.planoNome || plan.name || 'Plano sem nome';
    const isActive = assinatura.status === 1;
    const isUnico = assinatura.billingType === 'UNICO' || plan.periodicity === 'single';
    const isIndeterminate = !!plan.indeterminateQuantity;
    const price = assinatura.price ?? plan.price ?? 0;
    const services: PlanoService[] = plan.services || [];

    return (
        <div className="ass-page ass-detail-wrap px-2">
            <div className="ass-detail">
                <aside className="ass-detail-aside">
                    <div className="sf-group ass-summary">
                        <AvatarUser name={clientName} photoURL={undefined} size="h-12 w-12 !text-base" />
                        <div className="min-w-0 flex-1">
                            <h2 className="ass-summary-name">{clientName}</h2>
                            <div className="ass-sub">{planName}</div>
                        </div>
                        <span className="sf-tag shrink-0" data-tone={isActive ? 'green' : 'gray'}>
                            {isActive ? 'Ativa' : 'Cancelada'}
                        </span>
                    </div>

                    <div className="sf-group">
                        <div className="sf-info-row">
                            <span>Valor</span>
                            <span>
                                {formatCurrencyBRL(price)}
                                {!isUnico && getPlanPriceSuffix(plan.periodicity)}
                            </span>
                        </div>
                        <div className="sf-info-row">
                            <span>Cobrança</span>
                            <span>{isUnico ? 'Pagamento único' : getPlanBillingLabel(plan)}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Início</span>
                            <span>{formatCivilDateBR(assinatura.startAt)}</span>
                        </div>
                        {isActive && !isUnico && (
                            <div className="sf-info-row">
                                <span>Próxima cobrança</span>
                                <span>{formatCivilDateBR(assinatura.nextBillingAt)}</span>
                            </div>
                        )}
                        <div className="sf-info-row">
                            <span>Término</span>
                            <span>
                                {assinatura.endAt
                                    ? formatCivilDateBR(assinatura.endAt)
                                    : isIndeterminate
                                        ? 'Até cancelar'
                                        : '—'}
                            </span>
                        </div>
                        <div className="sf-info-row">
                            <span>Último pagamento</span>
                            <span>
                                {assinatura.lastPaymentAt ? (
                                    <>
                                        {formatCurrencyBRL(assinatura.lastPaymentAmount)} em{' '}
                                        {formatDateBR(assinatura.lastPaymentAt)}
                                    </>
                                ) : (
                                    'Nenhum'
                                )}
                                {!!assinatura.lastBillingStatus && (
                                    <span className="mt-1 block">
                                        <BillingStatusTag status={assinatura.lastBillingStatus} />
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className="sf-info-row">
                            <span>Total pago</span>
                            <span>{formatCurrencyBRL(assinatura.totalPaidAmount)}</span>
                        </div>
                        {isActive && !isUnico && !isIndeterminate && (
                            <div className="sf-info-row">
                                <span>A pagar</span>
                                <span>
                                    {formatCurrencyBRL(assinatura.totalRemainingAmount)} ·{' '}
                                    {assinatura.remainingPeriodsCount ?? 0}{' '}
                                    {assinatura.remainingPeriodsCount === 1 ? 'período' : 'períodos'}
                                </span>
                            </div>
                        )}
                    </div>

                    {services.length > 0 && (
                        <div>
                            <h3 className="sf-group-title">Serviços incluídos</h3>
                            <div className="sf-group">
                                {services.map((service, index) => (
                                    <div key={`${service.serviceId}-${index}`} className="sf-info-row">
                                        <span className="min-w-0 !flex-shrink">
                                            {service.serviceName || servicesDict[service.serviceId] || 'Serviço'}
                                        </span>
                                        <span className="shrink-0">
                                            {service.quantity}x {RECURRENCE_LABELS[service.recurrence] || service.recurrence}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {canManage && (
                        <div className="sf-group">
                            {isActive && (
                                <div className="sf-info-row !p-0">
                                    <button
                                        type="button"
                                        className="sf-destructive-row"
                                        onClick={() => setConfirmAction('cancel')}
                                    >
                                        Cancelar assinatura
                                    </button>
                                </div>
                            )}
                            <div className="sf-info-row !p-0">
                                <button
                                    type="button"
                                    className="sf-destructive-row"
                                    onClick={() => setConfirmAction('remove')}
                                >
                                    Remover assinatura
                                </button>
                            </div>
                        </div>
                    )}
                </aside>

                <section className="ass-detail-main">
                    <div className="sf-segmented mb-4" role="group" aria-label="Detalhes da assinatura">
                        <button
                            type="button"
                            aria-pressed={action === 'periodos'}
                            onClick={() => setParams({ action: 'periodos', id })}
                        >
                            Períodos
                        </button>
                        <button
                            type="button"
                            aria-pressed={action === 'recorrencias'}
                            onClick={() => setParams({ action: 'recorrencias', id })}
                        >
                            Recorrências
                        </button>
                    </div>

                    {action === 'periodos' ? (
                        <AssinaturasPeriodos
                            assinatura={assinatura}
                            servicesDict={servicesDict}
                            onChanged={() => {
                                loadAssinatura();
                                reloadData();
                            }}
                        />
                    ) : (
                        <AssinaturasRecorrencias assinatura={assinatura} />
                    )}
                </section>
            </div>

            {confirmAction && (
                <Modal
                    title={confirmAction === 'cancel' ? 'Cancelar assinatura?' : 'Remover assinatura?'}
                    onClose={() => {
                        if (!processing) setConfirmAction(null);
                    }}
                    desktopClassName="sm:!max-w-md"
                >
                    <p className="text-sm text-base-content/70">
                        {confirmAction === 'cancel'
                            ? `A assinatura de ${clientName} será encerrada e não poderá ser reativada; para voltar, é preciso criar outra. As recorrências dela são removidas e os agendamentos em aberto, cancelados.`
                            : `A assinatura de ${clientName} sairá da lista. ${isActive ? 'Ela também será cancelada: as' : 'As'} recorrências dela são removidas e os agendamentos em aberto, cancelados.`}
                    </p>
                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setConfirmAction(null)}
                            disabled={processing}
                        >
                            Voltar
                        </button>
                        <button type="button" className="btn btn-error" onClick={handleConfirm} disabled={processing}>
                            {processing && <span className="loading loading-spinner loading-sm"></span>}
                            {confirmAction === 'cancel' ? 'Cancelar assinatura' : 'Remover assinatura'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
